import { Download, IdCard, MessageCircle, Phone } from 'lucide-react';
import type { FilaDeCartera } from '@/api/cartera.api';
import { DataTable, type Agrupacion } from '@/components/datos/DataTable';
import { csvDe, descargarCsv } from '@/components/datos/tabla-pura';
import type { EdicionDeClientes } from '@/hooks/useEdicionDeClientes';
import { enlaceTel } from '@/lib/cartera';
import type { EditableLeadField } from '@/lib/lead-fields';
import type { PrefCartera } from '@/lib/pref-cartera';
import { agrupacionDe, columnasALaVista, gruposDeEtapa, resumenDeGrupo } from '@/lib/tablero-clientes';
import { DesplegableDeLote, OpcionesDeAsignar } from './CeldasDeCliente';
import { columnasDeClientes } from './columnas-de-clientes';

/**
 * LA TABLA DE «CLIENTES» (tanda 6; «la tabla de Monday» que pidió Kervens, a la medida de un
 * equipo de Enrique): los clientes en grupos con su color y su resumen, cada dato se cambia
 * en su celda, y con varias filas marcadas se les cambia la etapa, se asignan o se exportan.
 *
 * No pide ni guarda nada: recibe las filas ya filtradas y ordenadas, y `edicion`
 * (`useEdicionDeClientes`) es quien guarda. Por eso se puede pintar en una prueba.
 *
 * No trae barra propia: agrupar, elegir columnas, la densidad y exportar viven en la barra de
 * herramientas de la pantalla (`BarraDeClientes`), que es una sola para las cuatro vistas.
 */
type PrefDeTabla = Pick<PrefCartera, 'agrupar' | 'columnas' | 'densidad'>;

export interface TablaDeClientesProps {
  /** Ya filtradas, buscadas y ordenadas por la pantalla. */
  filas: FilaDeCartera[];
  etapas: readonly string[];
  ahora: Date;
  /** Los campos de la ficha del negocio (`useCrmView`): cada uno es una columna, con el editor de su tipo. */
  campos: EditableLeadField[];
  /** Las temperaturas con el nombre que les da el negocio (`leadStatusOptionsFrom`). */
  temperaturas: Array<{ value: string; label: string }>;
  /** Por qué se agrupa, qué columnas se ven y en qué orden (`[]` = las de entrada) y la densidad. */
  pref: PrefDeTabla;
  edicion: EdicionDeClientes;
  onVerFicha: (fila: FilaDeCartera) => void;
  /** Qué hace «Abrir el chat» con cada uno (`useAbrirChat`); `undefined` = no hay a dónde ir. */
  chatDe: (fila: FilaDeCartera) => (() => void) | undefined;
  /** T14: las fuentes de la cartera (`fuentesDe`), para agrupar por fuente y para «Cambiar fuente…». */
  fuentes?: readonly string[];
}

/** El valor de «Otra fuente…» en el desplegable del lote: pide el nombre. */
export const OTRA_FUENTE = '__otra__';

// Sin agrupar sigue siendo un tablero: un solo grupo con todos, con su contador y su resumen.
const TODOS = { de: () => 'todos', grupos: [{ clave: 'todos', etiqueta: 'Todos', color: 'bg-brand-600' }] };
const idDe = (fila: FilaDeCartera) => fila.contactId;

export function TablaDeClientes({ filas, etapas, ahora, campos, temperaturas, pref, edicion, onVerFicha, chatDe, fuentes = [] }: TablaDeClientesProps) {
  const { asignables } = edicion;
  const etapasConColor = gruposDeEtapa(etapas);
  // Ya en el orden del usuario: la tabla dibuja las columnas como vienen.
  const columnas = columnasDeClientes({ campos, etapas, temperaturas, ahora, edicion, onVerFicha, guardadas: pref.columnas });
  const aLaVista = columnasALaVista(pref.columnas);

  const agrupacion: Agrupacion<FilaDeCartera> = {
    ...(agrupacionDe(pref.agrupar, etapas, asignables, fuentes) ?? TODOS),
    pie: (delGrupo) => <span title="Cuántas parejas son y cuántas ya pagaron su entrada">{resumenDeGrupo(delGrupo)}</span>,
  };

  return (
    <DataTable
      filas={filas}
      columnas={columnas}
      idDe={idDe}
      memoria="cartera"
      agrupacion={agrupacion}
      columnasVisibles={aLaVista}
      densidad={pref.densidad}
      sinBarraPropia
      onAbrirFila={onVerFicha}
      accionesDeFila={[
        { etiqueta: 'Ver ficha', icono: <IdCard className="h-4 w-4" />, onClick: onVerFicha },
        { etiqueta: 'Abrir el chat', icono: <MessageCircle className="h-4 w-4" />, onClick: (f) => chatDe(f)?.(), ocultaSi: (f) => !chatDe(f) },
        {
          etiqueta: 'Llamar',
          icono: <Phone className="h-4 w-4" />,
          onClick: (f) => {
            const tel = enlaceTel(f.telefono);
            if (tel) window.location.href = tel;
          },
          ocultaSi: (f) => !enlaceTel(f.telefono),
        },
      ]}
      accionesEnLote={[
        {
          etiqueta: 'Cambiar etapa…',
          control: (marcadas) => (
            <DesplegableDeLote rotulo="Cambiar etapa…" ocupado={edicion.ocupado} onElegir={(etapa) => edicion.etapaEnLote(marcadas, etapa)}>
              {etapasConColor.map((g) => (
                <option key={g.clave} value={g.clave}>
                  {g.etiqueta}
                </option>
              ))}
            </DesplegableDeLote>
          ),
        },
        {
          etiqueta: 'Asignar a…',
          control: (marcadas) => (
            <DesplegableDeLote rotulo="Asignar a…" ocupado={edicion.ocupado} onElegir={(valor) => edicion.asignarEnLote(marcadas, valor)}>
              <OpcionesDeAsignar asignables={asignables} />
            </DesplegableDeLote>
          ),
        },
        {
          etiqueta: 'Cambiar fuente…',
          control: (marcadas) => (
            <DesplegableDeLote
              rotulo="Cambiar fuente…"
              ocupado={edicion.ocupado}
              onElegir={(valor) => {
                const fuente = valor === OTRA_FUENTE ? window.prompt('¿De qué base vienen? (por ejemplo: «Promoción octubre»)') : valor;
                if (fuente?.trim()) edicion.fuenteEnLote(marcadas, fuente);
              }}
            >
              {fuentes.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
              <option value={OTRA_FUENTE}>Otra fuente…</option>
            </DesplegableDeLote>
          ),
        },
        {
          etiqueta: 'Exportar',
          icono: <Download className="h-3.5 w-3.5" />,
          // Lo marcado, con las columnas que se están viendo.
          onClick: (marcadas) => descargarCsv('clientes', csvDe(marcadas, columnas.filter((c) => c.fija || aLaVista.includes(c.id)))),
        },
      ]}
    />
  );
}
